/**
 * Ad-click attribution capture.
 *
 * On landing (App mount), read ?gclid= and ?utm_* from the URL and store in
 * a first-party cookie. On first authenticated session, persist the captured
 * values to the user's profiles row so server-side offline conversion uploads
 * (PR 2) can read gclid from the DB instead of the client.
 *
 * Consent: capture runs only after advertising consent is granted. Pre-consent
 * we keep the raw URL params in session state; when consent flips to granted,
 * we commit to cookies.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';
import { normalizeTrackableGclid } from '@/lib/googleAdsClickId';

const GCLID_COOKIE = '_fab_gclid';
const UTM_COOKIE = '_fab_utm';
const CAPTURED_AT_COOKIE = '_fab_gclid_at';
const NINETY_DAYS_SECONDS = 60 * 60 * 24 * 90;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
type UtmKey = (typeof UTM_KEYS)[number];

type CapturedAttribution = {
  gclid: string | null;
  gclidCapturedAt: string | null;
  utm: Partial<Record<UtmKey, string>>;
};

/**
 * Read current attribution from URL search params. Does not touch storage.
 */
function readFromUrl(): CapturedAttribution {
  if (typeof window === 'undefined') {
    return { gclid: null, gclidCapturedAt: null, utm: {} };
  }
  const params = new URLSearchParams(window.location.search);
  const gclid = normalizeTrackableGclid(params.get('gclid'));
  const utm: Partial<Record<UtmKey, string>> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
    }
  }
  return {
    gclid,
    gclidCapturedAt: gclid ? new Date().toISOString() : null,
    utm,
  };
}

function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') {
    return;
  }
  const isSecure = window.location.protocol === 'https:';
  const attrs = [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${maxAgeSeconds}`,
    'SameSite=Lax',
  ];
  if (isSecure) {
    attrs.push('Secure');
  }
  document.cookie = attrs.join('; ');
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const pattern = new RegExp(`(?:^|; )${name}=([^;]*)`);
  const match = document.cookie.match(pattern);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Persist captured attribution to first-party cookies. Called only after
 * advertising consent is granted.
 */
function writeToCookies(captured: CapturedAttribution): void {
  if (captured.gclid) {
    setCookie(GCLID_COOKIE, captured.gclid, NINETY_DAYS_SECONDS);
    if (captured.gclidCapturedAt) {
      setCookie(CAPTURED_AT_COOKIE, captured.gclidCapturedAt, NINETY_DAYS_SECONDS);
    }
  }
  if (Object.keys(captured.utm).length > 0) {
    setCookie(UTM_COOKIE, JSON.stringify(captured.utm), NINETY_DAYS_SECONDS);
  }
}

/**
 * Read previously persisted attribution from cookies. Returns null gclid if
 * nothing is stored.
 */
export function readPersistedAttribution(): CapturedAttribution {
  const gclid = normalizeTrackableGclid(readCookie(GCLID_COOKIE));
  const gclidCapturedAt = readCookie(CAPTURED_AT_COOKIE);
  const utmRaw = readCookie(UTM_COOKIE);
  let utm: Partial<Record<UtmKey, string>> = {};
  if (utmRaw) {
    try {
      const parsed = JSON.parse(utmRaw);
      if (parsed && typeof parsed === 'object') {
        utm = parsed;
      }
    } catch {
      // Ignore malformed cookie
    }
  }
  return { gclid, gclidCapturedAt, utm };
}

/**
 * Capture attribution from the current URL. Call on every App mount AND when
 * advertising consent becomes granted (so we don't lose a gclid that arrived
 * before consent). Idempotent — cookies are only written when consent is ok
 * AND the URL currently carries attribution.
 */
export function captureAdClickAttribution(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const consentGranted = window.__fabGaAdvertisingConsentGranted === true;
  if (!consentGranted) {
    return;
  }
  const captured = readFromUrl();
  if (!captured.gclid && Object.keys(captured.utm).length === 0) {
    return;
  }
  // First-touch: don't overwrite an existing gclid with one from a later visit.
  const existing = readPersistedAttribution();
  const merged: CapturedAttribution = {
    gclid: existing.gclid ?? captured.gclid,
    gclidCapturedAt: existing.gclidCapturedAt ?? captured.gclidCapturedAt,
    utm: { ...captured.utm, ...existing.utm },
  };
  writeToCookies(merged);
}

/**
 * Write captured attribution to the current user's profiles row, first-touch
 * only. Safe to call on every auth change — if profile.gclid is already set,
 * it won't be overwritten.
 */
export async function commitAttributionToProfile(
  supabase: SupabaseClient,
  userId: string,
): Promise<void> {
  const captured = readPersistedAttribution();
  if (!captured.gclid && Object.keys(captured.utm).length === 0) {
    return;
  }

  try {
    const { data: profile, error: fetchErr } = await supabase
      .from('profiles')
      .select('gclid')
      .eq('id', userId)
      .single();

    if (fetchErr) {
      logger.debug('gclidCapture: could not fetch profile for attribution commit', fetchErr);
      return;
    }

    const existingRawGclid = (profile as { gclid?: string | null } | null)?.gclid ?? null;
    const existingValidGclid = normalizeTrackableGclid(existingRawGclid);

    // First-touch attribution: keep an existing valid gclid, but allow a newly
    // captured valid gclid to replace legacy junk data from older builds/tests.
    if (existingValidGclid) {
      return;
    }

    const updates: Record<string, string | null> = {};
    if (captured.gclid) {
      updates.gclid = captured.gclid;
      updates.gclid_captured_at = captured.gclidCapturedAt ?? new Date().toISOString();
    }
    for (const key of UTM_KEYS) {
      const value = captured.utm[key];
      if (value) {
        updates[key] = value;
      }
    }

    if (Object.keys(updates).length === 0) {
      return;
    }

    const { error: updateErr } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId);

    if (updateErr) {
      logger.debug('gclidCapture: profile attribution update failed', updateErr);
    }
  } catch (err) {
    logger.debug('gclidCapture: unexpected error committing attribution', err);
  }
}
